<script lang="ts">
  import { untrack } from 'svelte';
  import MetalFrame from './hud/MetalFrame.svelte';
  import type { SoundStore } from '../audio/sound-store';
  import type { SettingsStore } from '../settings/settings-store';
  import {
    BACKGROUND_STYLES,
    backgroundStore,
    type BackgroundStyle,
  } from '../assets/background-store';

  interface Props {
    sounds: SoundStore;
    settings: SettingsStore;
    onclose: () => void;
  }

  const { sounds, settings, onclose }: Props = $props();

  type Tab = 'sound' | 'display' | 'game';

  const TABS: readonly { readonly id: Tab; readonly label: string }[] = [
    { id: 'sound', label: 'Sound' },
    { id: 'display', label: 'Display' },
    { id: 'game', label: 'Game' },
  ];

  let tab = $state<Tab>('sound');

  let enabled = $state(untrack(() => sounds.enabled));
  let master = $state(untrack(() => sounds.volume));
  let music = $state(untrack(() => sounds.musicVolume));
  let effects = $state(untrack(() => sounds.effectsVolume));
  let background = $state(untrack(() => backgroundStore.style));
  let showFps = $state(untrack(() => settings.get('showFps')));
  let confirmLeave = $state(untrack(() => settings.get('confirmLeave')));
  let idleMenu = $state(untrack(() => settings.get('idleMenu')));
  let skipIntro = $state(untrack(() => settings.get('skipIntro')));

  $effect(() =>
    sounds.subscribe(() => {
      enabled = sounds.enabled;
      master = sounds.volume;
      music = sounds.musicVolume;
      effects = sounds.effectsVolume;
    }),
  );

  let root: HTMLDivElement | undefined = $state();

  $effect(() => {
    root?.focus();
  });

  function percent(value: number): string {
    return `${String(Math.round(value * 100))}%`;
  }

  function level(event: Event): number {
    const input = event.currentTarget;
    return input instanceof HTMLInputElement ? Number(input.value) / 100 : 0;
  }

  function setEnabled(on: boolean): void {
    sounds.resume();
    sounds.enabled = on;
  }

  function setBackground(style: BackgroundStyle): void {
    background = style;
    backgroundStore.style = style;
  }

  function onkeydown(event: KeyboardEvent): void {
    // The game's bindings live on `window`: unstopped, a digit sends an emoji and Escape suicides.
    event.stopPropagation();
    if (event.code === 'Escape') {
      event.preventDefault();
      onclose();
    }
  }
</script>

<div
  class="settings"
  bind:this={root}
  role="dialog"
  aria-modal="true"
  aria-label="Settings"
  data-testid="settings-screen"
  tabindex="-1"
  {onkeydown}
  onpointerdown={(event) => {
    if (event.target === event.currentTarget) event.preventDefault();
  }}
>
  <div class="settings__panel">
    <MetalFrame />

    <h2 class="settings__heading">Settings</h2>

    <div class="settings__well settings__rail">
      {#each TABS as item (item.id)}
        <button
          class="wis-tab settings__tab"
          type="button"
          aria-pressed={tab === item.id}
          data-testid="settings-tab-{item.id}"
          onclick={() => {
            tab = item.id;
          }}>{item.label}</button
        >
      {/each}
    </div>

    <div class="settings__well settings__body">
      {#if tab === 'sound'}
        <div class="settings__row">
          <span class="settings__label">Sound</span>
          <span class="settings__control">
            <button
              class="wis-btn-square settings__toggle"
              type="button"
              aria-pressed={enabled}
              onclick={() => {
                setEnabled(true);
              }}>On</button
            >
            <button
              class="wis-btn-square settings__toggle"
              type="button"
              aria-pressed={!enabled}
              onclick={() => {
                setEnabled(false);
              }}>Off</button
            >
          </span>
        </div>

        <div class="settings__row">
          <label class="settings__label" for="volume-master">Master</label>
          <span class="settings__control">
            <input
              id="volume-master"
              class="settings__slider"
              type="range"
              min="0"
              max="100"
              value={Math.round(master * 100)}
              style:--wis-level="{Math.round(master * 100)}%"
              data-testid="settings-master"
              oninput={(event) => {
                sounds.volume = level(event);
              }}
            />
            <span class="settings__value">{percent(master)}</span>
          </span>
        </div>

        <div class="settings__row">
          <label class="settings__label" for="volume-music">Music</label>
          <span class="settings__control">
            <input
              id="volume-music"
              class="settings__slider"
              type="range"
              min="0"
              max="100"
              value={Math.round(music * 100)}
              style:--wis-level="{Math.round(music * 100)}%"
              data-testid="settings-music"
              oninput={(event) => {
                sounds.musicVolume = level(event);
              }}
            />
            <span class="settings__value">{percent(music)}</span>
          </span>
        </div>

        <div class="settings__row">
          <label class="settings__label" for="volume-effects">Effects</label>
          <span class="settings__control">
            <input
              id="volume-effects"
              class="settings__slider"
              type="range"
              min="0"
              max="100"
              value={Math.round(effects * 100)}
              style:--wis-level="{Math.round(effects * 100)}%"
              data-testid="settings-effects"
              oninput={(event) => {
                sounds.effectsVolume = level(event);
              }}
            />
            <span class="settings__value">{percent(effects)}</span>
          </span>
        </div>
      {:else if tab === 'display'}
        <div class="settings__row">
          <span class="settings__label">Background</span>
          <span class="settings__control">
            <select
              class="wis-select settings__select"
              data-testid="settings-background"
              value={background}
              onchange={(event) => {
                setBackground(event.currentTarget.value as BackgroundStyle);
              }}
            >
              {#each BACKGROUND_STYLES as style (style)}
                <option value={style}>{style.toUpperCase()}</option>
              {/each}
            </select>
          </span>
        </div>

        <label class="settings__check">
          <input
            type="checkbox"
            checked={showFps}
            data-testid="settings-fps"
            onchange={(event) => {
              showFps = event.currentTarget.checked;
              settings.set('showFps', showFps);
            }}
          />
          <span>Show frame rate</span>
        </label>

        <label class="settings__check">
          <input
            type="checkbox"
            checked={idleMenu}
            data-testid="settings-idle-menu"
            onchange={(event) => {
              idleMenu = event.currentTarget.checked;
              settings.set('idleMenu', idleMenu);
            }}
          />
          <span>Lower main menu framerate</span>
        </label>

        <label class="settings__check">
          <input
            type="checkbox"
            checked={skipIntro}
            data-testid="settings-skip-intro"
            onchange={(event) => {
              skipIntro = event.currentTarget.checked;
              settings.set('skipIntro', skipIntro);
            }}
          />
          <span>Skip the intro</span>
        </label>
      {:else}
        <label class="settings__check">
          <input
            type="checkbox"
            checked={confirmLeave}
            data-testid="settings-confirm-leave"
            onchange={(event) => {
              confirmLeave = event.currentTarget.checked;
              settings.set('confirmLeave', confirmLeave);
            }}
          />
          <span>Confirm before leaving</span>
        </label>
        <p class="settings__note">
          Asks before a reload or a closed tab drops you out of a match in progress.
        </p>
      {/if}
    </div>

    <button
      class="wis-btn settings__close"
      type="button"
      data-testid="settings-close"
      onclick={onclose}>Close</button
    >
  </div>
</div>

<style>
  .settings {
    position: fixed;
    inset: 0;
    z-index: 10;
    background: rgb(0 0 0 / 55%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--wis-font);
    pointer-events: auto;
    outline: none;
  }

  .settings__panel {
    position: relative;
    width: min(620px, 94vw);
    height: min(320px, 92vh);
    color: var(--wis-ink);
  }

  .settings__heading {
    position: absolute;
    top: 26px;
    left: 0;
    right: 0;
    margin: 0;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 3px;
    font-size: 20px;
    font-weight: normal;
  }

  .settings__well {
    position: absolute;
    top: 62px;
    bottom: 52px;
    background:
      repeating-linear-gradient(45deg, rgb(255 255 255 / 5%) 0 2px, transparent 2px 4px),
      repeating-linear-gradient(-45deg, rgb(0 0 0 / 38%) 0 2px, transparent 2px 4px),
      linear-gradient(#242423, #171716);
    box-shadow:
      inset 0 2px 5px rgb(0 0 0 / 85%),
      inset 0 -1px 0 rgb(255 255 255 / 14%),
      0 1px 0 rgb(255 255 255 / 22%);
  }

  .settings__rail {
    left: 44px;
    width: 150px;
    padding: 12px 10px 0;
    background: linear-gradient(
      rgb(255 255 255 / 22%),
      rgb(255 255 255 / 4%) 40%,
      rgb(0 0 0 / 16%)
    );
    box-shadow:
      inset 0 1px 0 rgb(255 255 255 / 55%),
      inset 0 -1px 0 rgb(0 0 0 / 45%),
      0 4px 9px rgb(0 0 0 / 45%);
  }

  .settings__tab {
    margin-bottom: 7px;
    padding: 7px 0;
    color: #f2f2f2;
    font-size: 14px;
    letter-spacing: 1px;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .settings__body {
    left: 208px;
    right: 44px;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow-y: auto;
  }

  .settings__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 38px;
  }

  .settings__label {
    flex: none;
    color: #e2e2e2;
    text-transform: uppercase;
    font-size: 13px;
    text-align: left;
  }

  .settings__control {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .settings__select {
    font-size: 13px;
  }

  /* The square plate art is drawn for a 30px pad; these are label-sized. */
  .settings__toggle {
    padding: 10px 22px;
    color: #f2f2f2;
    font-size: 11px;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .settings__value {
    width: 44px;
    flex: none;
    color: #e2e2e2;
    font-size: 12px;
    text-align: right;
  }

  .settings__slider {
    width: 150px;
    height: 26px;
    margin: 0;
    background: transparent;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
  }

  .settings__slider::-webkit-slider-runnable-track {
    height: 12px;
    background:
      linear-gradient(#f2f2f2, #9a9a9a 55%, #cfcfcf) 0 / var(--wis-level, 0%) 100% no-repeat,
      linear-gradient(#2f2f2f, #545454 55%, #3a3a3a);
    border: 1px solid #1e1e1e;
    box-shadow: inset 0 1px 2px rgb(0 0 0 / 70%);
  }

  .settings__slider::-moz-range-track {
    height: 12px;
    background:
      linear-gradient(#f2f2f2, #9a9a9a 55%, #cfcfcf) 0 / var(--wis-level, 0%) 100% no-repeat,
      linear-gradient(#2f2f2f, #545454 55%, #3a3a3a);
    border: 1px solid #1e1e1e;
    box-shadow: inset 0 1px 2px rgb(0 0 0 / 70%);
  }

  .settings__slider::-webkit-slider-thumb {
    appearance: none;
    -webkit-appearance: none;
    width: 15px;
    height: 26px;
    margin-top: -8px;
    background: linear-gradient(#f4f4f4, #b6b6b6 45%, #7d7d7d);
    border: 1px solid #1c1c1c;
    box-shadow:
      inset 0 1px 0 rgb(255 255 255 / 80%),
      0 2px 3px rgb(0 0 0 / 55%);
  }

  .settings__slider::-moz-range-thumb {
    width: 15px;
    height: 26px;
    border-radius: 0;
    background: linear-gradient(#f4f4f4, #b6b6b6 45%, #7d7d7d);
    border: 1px solid #1c1c1c;
    box-shadow:
      inset 0 1px 0 rgb(255 255 255 / 80%),
      0 2px 3px rgb(0 0 0 / 55%);
  }

  .settings__check {
    display: flex;
    color: #e2e2e2;
    align-items: center;
    gap: 10px;
    min-height: 38px;
    font-size: 13px;
    text-transform: uppercase;
    cursor: pointer;
  }

  .settings__check input {
    display: grid;
    place-items: center;
    width: 17px;
    height: 17px;
    margin: 0;
    appearance: none;
    background: linear-gradient(#cfcfcf, #8f8f8f);
    border: 1px solid #2a2a2a;
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 55%);
    cursor: pointer;
  }

  .settings__check input:checked {
    background: linear-gradient(#5a5a5a, #333);
  }

  .settings__check input:checked::after {
    width: 4px;
    height: 9px;
    border: solid #eaeaea;
    border-width: 0 2px 2px 0;
    transform: translateY(-1px) rotate(45deg);
    content: '';
  }

  .settings__note {
    margin: 0 0 14px;
    color: #9e9e9e;
    font-size: 12px;
    text-transform: none;
    line-height: 1.5;
  }

  /* Sized and hung to match the evolution panel's close, which players see far more often. */
  .settings__close {
    position: absolute;
    bottom: -15px;
    left: calc(50% - 75px);
    width: 150px;
    height: 48px;
    padding: 0;
    color: #f2f2f2;
    font-size: 19px;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }
</style>
